import { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { FormField } from '../shared/FormField';
import { NativeSelect } from '../shared/NativeSelect';
import { NATIVE_FIELD_CLASS } from '@/contabilidad/constants/ui';
import { aplanarAgrupadores } from '../../utils/agrupadores';
import { cn } from '@/lib/utils';
import type { Agrupador, GuardarAgrupadorPayload } from '../../types';

interface Props {
  open: boolean;
  agrupadores: Agrupador[];
  /** Agrupador a editar; null para crear uno nuevo. */
  agrupador?: Agrupador | null;
  /** Padre sugerido al crear un subagrupador. */
  parentIdInicial?: number | null;
  isSubmitting?: boolean;
  onSubmit: (payload: GuardarAgrupadorPayload) => Promise<void>;
  onClose: () => void;
}

export function AgrupadorEditorModal({ open, agrupadores, agrupador, parentIdInicial = null, isSubmitting = false, onSubmit, onClose }: Props) {
  const [nombre, setNombre] = useState('');
  const [parentId, setParentId] = useState('');
  const [error, setError] = useState(false);

  const isEditing = !!agrupador;
  // Un agrupador no puede quedar dentro de sí mismo ni de sus descendientes.
  const opciones = useMemo(() => aplanarAgrupadores(agrupadores, agrupador?.id), [agrupadores, agrupador]);

  useEffect(() => {
    if (!open) return;
    setNombre(agrupador?.nombre ?? '');
    const padre = agrupador ? agrupador.parentId : parentIdInicial;
    setParentId(padre === null ? '' : String(padre));
    setError(false);
  }, [open, agrupador, parentIdInicial]);

  async function handleSubmit() {
    if (!nombre.trim()) {
      setError(true);
      return;
    }
    await onSubmit({ nombre: nombre.trim(), parentId: parentId === '' ? null : Number(parentId) });
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle className="text-base font-semibold text-gray-900">
            {isEditing ? 'Editar agrupador' : 'Nuevo agrupador'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-1">
          <FormField label="Nombre" required>
            <input
              type="text"
              placeholder="Ej: Sellos, Impuestos, Inscripciones..."
              value={nombre}
              onChange={(e) => { setNombre(e.target.value); setError(false); }}
              className={cn(NATIVE_FIELD_CLASS, error && 'border-rose-500 focus:border-rose-500')}
              autoFocus
            />
            {error && <p className="text-xs text-rose-500 mt-0.5">El nombre es obligatorio.</p>}
          </FormField>

          <FormField label="Dentro de">
            <NativeSelect value={parentId} onChange={(e) => setParentId(e.target.value)}>
              <option value="">(Nivel principal)</option>
              {opciones.map((o) => (
                <option key={o.id} value={String(o.id)}>
                  {'  '.repeat(o.profundidad)}{o.nombre}
                </option>
              ))}
            </NativeSelect>
          </FormField>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={isSubmitting} className="rounded-full">
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting} className="rounded-full">
            {isEditing ? 'Guardar' : 'Crear'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
